import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MetricService } from '@patient-ui/patient-web/store';
import {
  ERROR_MSG_VERIFICATION_FAILED,
  ZIP_REGEX,
  paymentLettersValidationMessages,
} from '@patient-ui/shared/constants';
import { EventCode, ISplunkMetric, MetricKey } from '@patient-ui/shared/models';

import { PdfViewerService } from '../services/pdf-viewer.service';
import { dateValidator } from '../validators/date.validator';

/**
 * Identity verification for NACHA / card-capture payment letter PDFs.
 * Reuses the same DOB + ZIP UI as the estimate verify page (guest + registered).
 * Route: /renderpdf/payment-letters?transaction={encryptedTransactionId}
 *
 * The transaction parameter is forwarded to the backend untouched; the backend
 * decrypts it, derives the authorization type and enforces the 60-day window.
 */
@Component({
  selector: 'patient-ui-payment-letters-verify',
  templateUrl: './payment-letters-verify.component.html',
  styleUrls: ['./payment-letters-verify.component.scss'],
})
export class PaymentLettersVerifyComponent implements OnInit, OnDestroy {
  private readonly BLOB_URL_CLEANUP_DELAY_MS = 30000;
  private destroy$ = new Subject<void>();

  readonly validationMessages = paymentLettersValidationMessages;

  minDate = new Date();
  maxDate = new Date();

  patientForm!: UntypedFormGroup;
  btnSubmitSpin = false;
  showAlert$ = new BehaviorSubject<boolean>(false);
  alertStatus: 'danger' | 'warning' | 'info' = 'danger';
  errorMsg = '';

  transactionId = '';
  /** True when the link carries no transaction value at all; hides the form. */
  linkInvalid = false;

  private blobUrlCleanupTimer?: ReturnType<typeof setTimeout>;
  private currentBlobUrl?: string;

  private readonly DASHBOARD_ID = 'Guest_Receipt';
  constructor(
    private activatedRoute: ActivatedRoute,
    private pdfViewerService: PdfViewerService,
    private metricService: MetricService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.maxDate.setFullYear(this.maxDate.getFullYear());
    this.minDate.setFullYear(this.minDate.getFullYear() - 120);

    this.initForm();

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        // Some email clients rewrite `+` in the base64 payload as a space.
        const transaction = (params.get('transaction') || '').replace(
          / /g,
          '+'
        );
        this.readTransactionParam(transaction);
        this.logMetric(EventCode.receiptVerificationPageLoaded);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.blobUrlCleanupTimer) {
      clearTimeout(this.blobUrlCleanupTimer);
    }
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = undefined;
    }
  }

  onSubmit(): void {
    if (!this.patientForm.valid) {
      // Mark the form as dirty in case what's invalid is forgetting to populate a field
      Object.keys(this.patientForm.controls).forEach((key) => {
        this.patientForm.controls[key].markAsDirty();
      });
      this.patientForm.updateValueAndValidity();
      this.showAlert$.next(false);
      return;
    }

    this.performFormSubmission();
  }

  private performFormSubmission(): void {
    if (this.linkInvalid || !this.transactionId) {
      this.showAlert$.next(true);
      return;
    }

    this.showAlert$.next(false);
    this.btnSubmitSpin = true;
    this.cdr.detectChanges();
    this.logRenderMetric('attempted');

    const dob = this.formatDate(this.patientForm.get('dateOfBirth')?.value);
    const zip = (this.patientForm.get('zipCode')?.value || '').trim();

    this.pdfViewerService
      .getPaymentAuthorizationPdf(this.transactionId, dob, zip)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.btnSubmitSpin = false;
          this.cdr.detectChanges();

          // Check for empty PDF response
          if (!response.pdf || response.pdf.trim() === '') {
            this.logRenderMetric('failed');
            this.showVerificationFailed(response.reasonCode);
            return;
          }

          // HTTP 200 - successful with PDF
          // Convert base64 PDF to blob (guard against malformed base64)
          let blob: Blob;
          try {
            const byteCharacters = atob(response.pdf);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            blob = new Blob([byteArray], { type: 'application/pdf' });
          } catch {
            this.logRenderMetric('failed');
            this.showVerificationFailed();
            return;
          }

          // PDF received and decoded successfully
          this.logRenderMetric('success');
          this.openPdf(blob);
        },
        error: () => {
          // HTTP 400/404/500 - backend returned an error status
          this.btnSubmitSpin = false;
          this.logRenderMetric('failed');
          this.showVerificationFailed();
        },
      });
  }

  private showVerificationFailed(reasonCode?: string): void {
    this.alertStatus = 'danger';
    this.errorMsg = this.getErrorMessageForReasonCode(reasonCode);
    this.showAlert$.next(true);
    this.cdr.detectChanges();
  }

  private openPdf(blob: Blob): void {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }

    const blobUrl = URL.createObjectURL(blob);
    this.currentBlobUrl = blobUrl;

    const pdfWindow = window.open(blobUrl, '_blank');

    if (pdfWindow) {
      if (this.blobUrlCleanupTimer) {
        clearTimeout(this.blobUrlCleanupTimer);
      }
      this.blobUrlCleanupTimer = setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        this.currentBlobUrl = undefined;
      }, this.BLOB_URL_CLEANUP_DELAY_MS);
      return;
    }

    URL.revokeObjectURL(blobUrl);
    this.currentBlobUrl = undefined;
    this.logMetric(EventCode.receiptVerificationPopupBlocked);
    this.alertStatus = 'warning';
    this.errorMsg =
      'Unable to open PDF. Please check your browser settings and allow popups for this site.';
    this.showAlert$.next(true);
    this.cdr.detectChanges();
  }

  /**
   * Reports client-side rendering only — whether the browser decoded the base64
   * payload and opened it. The backend separately logs the authoritative
   * NACHA/capture download metrics and audit trail, since only it can decrypt
   * the transaction ID and knows the authorization type. Deliberately distinct
   * event codes so dashboards don't double-count one download as two.
   */
  private logRenderMetric(kind: 'attempted' | 'success' | 'failed'): void {
    const eventCodes: Record<'attempted' | 'success' | 'failed', EventCode> = {
      attempted: EventCode.receiptPdfRenderAttempted,
      success: EventCode.receiptPdfRendered,
      failed: EventCode.receiptPdfRenderFailed,
    };
    this.logMetric(eventCodes[kind]);
  }

  /**
   * Fire-and-forget metric log via MetricService (POST /guest/guest/metric).
   * Failures are
   * swallowed so analytics never block or break the PDF flow.
   */
  private logMetric(eventCode: EventCode): void {
    const metricList: ISplunkMetric[] = [
      { metricKey: MetricKey.eventCode, metricValue: eventCode },
      { metricKey: MetricKey.dashboardId, metricValue: this.DASHBOARD_ID },
      { metricKey: MetricKey.transactionId, metricValue: this.transactionId },
      {
        metricKey: MetricKey.pageUrl,
        metricValue: globalThis.location.pathname,
      },
    ];
    this.metricService
      .logSplunkMetric(metricList)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: () => undefined });
  }

  private initForm(): void {
    this.patientForm = new UntypedFormGroup(
      {
        dateOfBirth: new UntypedFormControl('', [
          Validators.required,
          dateValidator(this.maxDate, this.minDate),
        ]),
        zipCode: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(ZIP_REGEX),
        ]),
      },
      { updateOn: 'blur' }
    );
  }

  /**
   * The transaction parameter is an opaque, KMS-encrypted value. The date the
   * letter was generated and the authorization type live inside the encrypted
   * payload, so only the backend can read them — it owns decryption, format
   * validation and the 60-day expiration check. This forwards the value as-is.
   */
  private readTransactionParam(transactionParam: string): void {
    this.transactionId = transactionParam;
    this.linkInvalid = false;
    this.showAlert$.next(false);

    if (!transactionParam) {
      this.linkInvalid = true;
      this.alertStatus = 'danger';
      this.errorMsg = 'This authorization link is invalid.';
      this.showAlert$.next(true);
    }
  }

  /**
   * Maps backend reason codes to user-friendly error messages.
   * EPS Response Codes:
   *   200: Successful - Process completed as expected (with PDF)
   *   -200: Failure - Process failed
   *   -201: EPS Failure - Process failed due to EPS internal error
   *   -203: Unknown - Error due to unknown factors (also used for expired links)
   *   -204: Invalid Request - Invalid Request (also used for expired links)
   *   -205: Invalid Credentials - Date of birth or ZIP code validation failed (backend validation)
   *   -206: Link Expired - Link has exceeded the 60-day validity window
   *   -215: No Transaction Found
   */
  private getErrorMessageForReasonCode(reasonCode?: string): string {
    // Handle expired links (>60 days) - backend returns -203, -204, or -206
    if (
      reasonCode === '-203' ||
      reasonCode === '-204' ||
      reasonCode === '-206'
    ) {
      return ERROR_MSG_VERIFICATION_FAILED.letterExpired;
    }

    // All other error scenarios return the same user-friendly message
    return ERROR_MSG_VERIFICATION_FAILED.letterNotFound;
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    const slashMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      return `${slashMatch[3]}-${slashMatch[1]}-${slashMatch[2]}`;
    }

    if (/^\d{8}$/.test(dateString)) {
      return (
        dateString.substring(4) +
        '-' +
        dateString.substring(0, 2) +
        '-' +
        dateString.substring(2, 4)
      );
    }

    return dateString;
  }
}
